import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { brainApi, cloudStorageApi, oauthApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  Cloud, Loader2, CheckCircle2, Circle, AlertCircle, Folder, FileText, Search, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useAssistantName } from "@/hooks/useAssistantName";

const PROVIDER_LABELS = {
  google_drive: "Google Drive",
  sharepoint: "SharePoint",
};

const STEPS = [
  { id: 1, label: "Connect" },
  { id: 2, label: "Browse" },
  { id: 3, label: "Import" },
];

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ProgressHeader({ step, onBack, backDisabled = false }) {
  const canBack = step > 1 && !backDisabled;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <div className="flex items-center gap-1 min-w-0">
          {canBack ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center justify-center h-9 w-9 sm:h-7 sm:w-7 -ml-1 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              aria-label="Previous step"
              data-testid="cloud-wizard-back"
            >
              <ChevronLeft size={18} />
            </button>
          ) : (
            <span className="w-7 shrink-0" aria-hidden />
          )}
          <span>Step {Math.min(step, 3)} of 3</span>
        </div>
        <span>{STEPS[Math.min(step, 3) - 1]?.label}</span>
      </div>
      <div className="flex gap-1">
        {STEPS.map((s) => {
          let barClass = "bg-slate-200";
          if (step > s.id) barClass = "bg-emerald-600";
          else if (step === s.id) barClass = "bg-emerald-400";
          return (
            <div
              key={s.id}
              className={cn("h-1.5 flex-1 rounded-full transition-colors", barClass)}
            />
          );
        })}
      </div>
    </div>
  );
}

ProgressHeader.propTypes = {
  step: PropTypes.number.isRequired,
  onBack: PropTypes.func,
  backDisabled: PropTypes.bool,
};

function describeSync(sync, sourceDetail) {
  if (!sync && sourceDetail?.chunk_count > 0) {
    return { label: "Knowledge indexed in Sellix Brain", hint: "Agents recall uploaded content at reply time.", ok: true };
  }
  if (!sync) {
    return { label: "OpenClaw sync pending", hint: null, ok: false };
  }
  const st = sync.status;
  if (st === "completed") {
    return {
      label: "Knowledge indexed and ready for agents",
      hint: sync.reason || "Available via Sellix Brain search at reply time.",
      ok: true,
    };
  }
  if (st === "skipped") {
    return {
      label: "Knowledge indexed — gateway sync deferred",
      hint: sync.reason || "Gateway offline; sync runs when connected.",
      ok: true,
    };
  }
  if (st === "failed") {
    return {
      label: "Sync needs attention",
      hint: sync.reason || "Knowledge is still indexed in Sellix — retry sync.",
      ok: false,
    };
  }
  return { label: st || "Sync pending", hint: sync.reason, ok: st === "completed" || st === "skipped" };
}

const INITIAL_PROCESSING_FLAGS = {
  downloading: false,
  processing: false,
  chunked: false,
  embedded: false,
  indexed: false,
  synced: false,
};

function scheduleProcessingFlag(setFlags, signal, key, delay) {
  return new Promise((resolve) => {
    setTimeout(() => {
      if (!signal.aborted) {
        setFlags((f) => ({ ...f, [key]: true }));
      }
      resolve();
    }, delay);
  });
}

/** Animate pipeline steps while the import API runs (min visibility ~1.2s). */
async function animateImportProgress(setFlags, signal) {
  const stages = [
    ["downloading", 0],
    ["processing", 350],
    ["chunked", 700],
    ["embedded", 1050],
    ["indexed", 1400],
  ];
  await Promise.all(
    stages.map(([key, delay]) => scheduleProcessingFlag(setFlags, signal, key, delay)),
  );
  if (!signal.aborted) {
    await new Promise((r) => setTimeout(r, 200));
  }
}

function StepIndicator({ done, active, label, hint }) {
  let StatusIcon = Circle;
  let iconClass = "text-slate-300 shrink-0 mt-0.5";
  if (done) {
    StatusIcon = CheckCircle2;
    iconClass = "text-emerald-600 shrink-0 mt-0.5";
  } else if (active) {
    StatusIcon = Loader2;
    iconClass = "animate-spin text-emerald-600 shrink-0 mt-0.5";
  }
  return (
    <div className="flex items-start gap-3">
      <StatusIcon size={18} className={iconClass} />
      <div>
        <div className={cn("text-[13px] font-medium", done ? "text-slate-900" : "text-slate-700")}>{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

StepIndicator.propTypes = {
  done: PropTypes.bool,
  active: PropTypes.bool,
  label: PropTypes.string,
  hint: PropTypes.string,
};

function CloudBrowseItemList({
  busy,
  items,
  selected,
  searchMode,
  onToggleSelect,
  onOpenFolder,
}) {
  if (busy && items.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-slate-500">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-[12px] text-slate-500">No files here</div>
    );
  }
  return items.map((item) => {
    const Icon = item.type === "folder" ? Folder : FileText;
    const checked = Boolean(selected[item.id]);
    const openFolderMode = item.type === "folder" && !searchMode;
    return (
      <div
        key={item.id}
        className={cn(
          "flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-[12.5px]",
          checked && "bg-emerald-50/60",
        )}
        data-testid={`cloud-item-${item.id}`}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={() => onToggleSelect(item)}
        />
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer"
          onClick={() => {
            if (openFolderMode) onOpenFolder(item);
            else onToggleSelect(item);
          }}
        >
          <Icon size={14} className={item.type === "folder" ? "text-amber-600" : "text-slate-500"} />
          <span className="truncate font-medium text-slate-900">{item.name}</span>
          {item.type === "file" && (
            <span className="text-slate-400 shrink-0">{formatBytes(item.size_bytes)}</span>
          )}
        </button>
      </div>
    );
  });
}

CloudBrowseItemList.propTypes = {
  busy: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.object,
  searchMode: PropTypes.bool,
  onToggleSelect: PropTypes.func.isRequired,
  onOpenFolder: PropTypes.func.isRequired,
};

function DriveAccountBar({
  accountLabel,
  accounts,
  selectedConnectionId,
  busy,
  onAddAccount,
  onSwitchAccount,
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5 space-y-2"
      data-testid="cloud-drive-account-bar"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
            Google Drive account
          </div>
          <div className="text-[12.5px] font-medium text-slate-800 truncate mt-0.5">
            {accountLabel}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-[11px]"
            disabled={busy}
            onClick={onAddAccount}
            data-testid="cloud-add-account-btn"
          >
            Add account
          </Button>
        </div>
      </div>
      {accounts.length > 1 ? (
        <div className="flex items-center gap-2 text-[11px] text-slate-600">
          <span className="shrink-0">Switch to:</span>
          <select
            value={selectedConnectionId || ""}
            onChange={(e) => onSwitchAccount(e.target.value)}
            className="flex-1 h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px]"
            data-testid="cloud-account-select"
          >
            {accounts.map((acct) => (
              <option key={acct.id} value={acct.id}>
                {acct.account || acct.id}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="text-[10.5px] text-slate-500">
          Connect another Google account to browse files from a different Drive.
        </p>
      )}
    </div>
  );
}

function ConnectStatusBlock({
  connected,
  accounts,
  account,
  selectedConnectionId,
  busy,
  onSwitchAccount,
  onDisconnect,
}) {
  if (connected && accounts.length > 0) {
    return (
      <div className="space-y-2">
        <div className="text-[12px] font-medium text-slate-700">
          Connected Google accounts
        </div>
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
          {accounts.map((acct) => (
            <div
              key={acct.id}
              className="flex items-center justify-between gap-2 px-3 py-2 text-[12.5px]"
            >
              <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                <input
                  type="radio"
                  name="drive-account"
                  checked={selectedConnectionId === acct.id}
                  onChange={() => onSwitchAccount(acct.id)}
                  className="accent-emerald-600"
                />
                <span className="truncate text-slate-800">
                  {acct.account || "Google account"}
                </span>
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] text-rose-600 hover:text-rose-700 shrink-0"
                disabled={busy}
                onClick={() => onDisconnect(acct.id)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          Select which account to browse when importing files.
        </p>
      </div>
    );
  }
  if (connected) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px]">
        <div className="font-medium text-emerald-900">Connected</div>
        <div className="text-emerald-800 mt-0.5">{account || "Google account linked"}</div>
      </div>
    );
  }
  return (
    <p className="text-[13px] text-slate-600">
      Sign in with Google to browse and import documents from your Drive.
      This uses a separate permission from Gmail — read-only access to files.
      You can connect multiple Google accounts.
    </p>
  );
}

export function CloudImportWizard({
  open,
  onOpenChange,
  provider = "google_drive",
  onComplete,
  initialConnected = false,
}) {
  const assistantName = useAssistantName();
  const label = PROVIDER_LABELS[provider] || provider;
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState({ connected: false, account: null, accounts: [] });
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [folderId, setFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: "My Drive" }]);
  const [items, setItems] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [importResults, setImportResults] = useState([]);
  const [sourceId, setSourceId] = useState(null);
  const [sourceDetail, setSourceDetail] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [processingFlags, setProcessingFlags] = useState(INITIAL_PROCESSING_FLAGS);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);
  const wasOpenRef = useRef(false);

  const refreshConnectionStatus = useCallback(async () => {
    if (provider !== "google_drive") return null;
    try {
      const s = await cloudStorageApi.googleDriveStatus();
      setStatus(s);
      if (s?.connection_id) {
        setSelectedConnectionId((prev) => {
          const ids = (s.accounts || []).map((a) => a.id);
          if (prev && ids.includes(prev)) return prev;
          return s.connection_id;
        });
      } else {
        setSelectedConnectionId(null);
      }
      return s;
    } catch {
      setStatus({ connected: false, account: null, accounts: [] });
      setSelectedConnectionId(null);
      return null;
    }
  }, [provider]);

  const loadBrowse = useCallback(async (fid = null, token = null, opts = {}) => {
    const connId = opts.connectionId || selectedConnectionId;
    if (provider !== "google_drive" || !connId) return;
    setBusy(true);
    setError(null);
    try {
      let data;
      if (opts.search) {
        data = await cloudStorageApi.googleDriveSearch(opts.search, token, connId);
        setSearchMode(true);
      } else {
        data = await cloudStorageApi.googleDriveBrowse(fid, token, connId);
        setSearchMode(false);
      }
      setItems(token ? (prev) => [...prev, ...(data.items || [])] : (data.items || []));
      setNextPageToken(data.next_page_token || null);
    } catch (e) {
      setError(e.message || "Could not load files");
    } finally {
      setBusy(false);
    }
  }, [provider, selectedConnectionId]);

  // Reset wizard only when the dialog opens — not when step/busy changes mid-flow.
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    setStep(1);
    setSelected({});
    setImportResults([]);
    setSourceId(null);
    setSourceDetail(null);
    setSyncResult(null);
    setPendingFiles([]);
    setProcessingFlags(INITIAL_PROCESSING_FLAGS);
    setImporting(false);
    setImportComplete(false);
    setFolderId(null);
    setBreadcrumb([{ id: null, name: "My Drive" }]);
    setSearchQ("");
    setError(null);
    setBusy(false);

    refreshConnectionStatus()
      .then((s) => { setStep(s?.connected ? 2 : 1); })
      .catch(() => {});
  }, [open, initialConnected, refreshConnectionStatus]);

  // Initial browse when entering step 2 or switching account — not on folder navigation.
  useEffect(() => {
    if (open && step === 2 && status.connected && selectedConnectionId) {
      setFolderId(null);
      setBreadcrumb([{ id: null, name: "My Drive" }]);
      setItems([]);
      setSearchMode(false);
      loadBrowse(null, null, { connectionId: selectedConnectionId }).catch(() => {});
    }
  }, [open, step, status.connected, selectedConnectionId, loadBrowse]);

  const connectDrive = async (addAccount = false) => {
    setBusy(true);
    setError(null);
    try {
      const r = await oauthApi.startGoogleDrive("/brain", addAccount);
      if (r?.auth_url) {
        window.location.href = r.auth_url;
        return;
      }
      toast.error("Google OAuth is not configured on this server");
    } catch (e) {
      setError(e.message || "Could not start Google sign-in");
      toast.error(e.message || "Could not start Google sign-in");
    } finally {
      setBusy(false);
    }
  };

  const openFolder = (item) => {
    setFolderId(item.id);
    setBreadcrumb((prev) => [...prev, { id: item.id, name: item.name }]);
    setItems([]);
    setSearchMode(false);
    loadBrowse(item.id, null).catch(() => {});
  };

  const goBreadcrumb = (idx) => {
    const crumb = breadcrumb[idx];
    setBreadcrumb(breadcrumb.slice(0, idx + 1));
    setFolderId(crumb.id);
    setItems([]);
    setSearchMode(false);
    loadBrowse(crumb.id, null).catch(() => {});
  };

  const goToParentFolder = () => {
    if (breadcrumb.length <= 1) return;
    goBreadcrumb(breadcrumb.length - 2);
  };

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = item;
      return next;
    });
  };

  const runSearch = () => {
    if (!searchQ.trim()) {
      setSearchMode(false);
      loadBrowse(folderId, null).catch(() => {});
      return;
    }
    setItems([]);
    loadBrowse(null, null, { search: searchQ.trim() }).catch(() => {});
  };

  const _processImportResult = async (result, progressSignal) => {
    const results = result.results || [];
    const readyCount = results.filter((r) => r.status === "ready").length;
    const sync = result.openclaw_sync || null;
    const detail = result.source_id
      ? await brainApi.getCustomSource(result.source_id)
      : result.source;

    setImportResults(results);
    setSourceId(result.source_id);
    setSourceDetail(detail);
    setSyncResult(sync);

    const syncOk = !sync || ["completed", "skipped"].includes(sync.status);
    setProcessingFlags({
      downloading: true,
      processing: true,
      chunked: readyCount > 0,
      embedded: readyCount > 0,
      indexed: readyCount > 0,
      synced: syncOk && readyCount > 0,
    });

    if (!readyCount) {
      let msg = "No importable files found in your selection";
      if (results.length) {
        msg = results.find((r) => r.error)?.error || "Import failed for all selected files";
      }
      setError(msg);
      if (!results.length) toast.error(msg);
      setStep(3);
      setImporting(false);
      return false;
    }
    return true;
  };

  const runImport = async () => {
    const selection = Object.values(selected).map((item) => ({
      type: item.type,
      id: item.id,
      recursive: item.type === "folder",
    }));
    if (!selection.length) {
      setError("Select at least one file or folder");
      return;
    }

    const fileNames = Object.values(selected).map((item) => (
      item.type === "folder" ? `${item.name} (folder)` : item.name
    ));
    setPendingFiles(fileNames);
    setProcessingFlags(INITIAL_PROCESSING_FLAGS);
    setStep(3);
    setImporting(true);
    setBusy(true);
    setError(null);

    const progressSignal = { aborted: false };
    const progressTask = animateImportProgress(setProcessingFlags, progressSignal);

    try {
      const result = await cloudStorageApi.googleDriveImport({
        connection_id: selectedConnectionId,
        source_id: sourceId || undefined,
        source_name: `${label} import`,
        selection,
      });

      progressSignal.aborted = true;
      await progressTask;

      const isReady = await _processImportResult(result, progressSignal);
      if (!isReady) return;

      await new Promise((r) => setTimeout(r, 600));
      const assignedSourceId = result.source_id || sourceId;
      if (assignedSourceId) {
        try {
          await brainApi.assignCustomSource(assignedSourceId, { agent_ids: ["all"] });
        } catch (assignErr) {
          toast.error(assignErr.message || "Could not assign knowledge to all agents");
        }
      }
      setImportComplete(true);
      setStep(3);
    } catch (e) {
      progressSignal.aborted = true;
      const msg = e.message || "Import failed";
      setError(msg);
      toast.error(msg);
      setStep(3);
    } finally {
      setBusy(false);
      setImporting(false);
    }
  };

  const finish = async () => {
    onOpenChange(false);
    if (onComplete) await onComplete();
  };

  const selectedCount = Object.keys(selected).length;
  const accounts = status.accounts || [];
  const activeAccount = accounts.find((a) => a.id === selectedConnectionId);

  const switchAccount = (connectionId) => {
    setSelectedConnectionId(connectionId);
    setFolderId(null);
    setBreadcrumb([{ id: null, name: "My Drive" }]);
    setItems([]);
    setSelected({});
    setSearchQ("");
    setSearchMode(false);
  };

  const goToPreviousStep = () => {
    if (importing) return;
    setError(null);
    if (importComplete) {
      setImportComplete(false);
      setStep(2);
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const canGoBack = step > 1 && !importing;

  const disconnectAccount = async (connectionId) => {
    setBusy(true);
    setError(null);
    try {
      await cloudStorageApi.disconnectGoogleDrive(connectionId);
      toast.success("Google account disconnected");
      const s = await refreshConnectionStatus();
      if (!s?.connected) {
        setStep(1);
      } else if (connectionId === selectedConnectionId) {
        const nextId = s.connection_id;
        switchAccount(nextId);
      }
    } catch (e) {
      toast.error(e.message || "Could not disconnect account");
    } finally {
      setBusy(false);
    }
  };

  const driveAccountBar = status.connected ? (
    <DriveAccountBar
      accountLabel={activeAccount?.account || status.account || "Connected account"}
      accounts={accounts}
      selectedConnectionId={selectedConnectionId}
      busy={busy}
      onAddAccount={() => connectDrive(true)}
      onSwitchAccount={switchAccount}
    />
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!importing) onOpenChange(next); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-xl max-h-[90dvh] overflow-y-auto" data-testid="cloud-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud size={18} className="text-emerald-600" />
            Import from {label}
          </DialogTitle>
          <DialogDescription>
            Connect your account, pick files, and {assistantName} will index them into your knowledge base.
          </DialogDescription>
        </DialogHeader>

        <ProgressHeader step={step} onBack={goToPreviousStep} backDisabled={importing} />

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 py-2">
            <ConnectStatusBlock
              connected={status.connected}
              accounts={accounts}
              account={status.account}
              selectedConnectionId={selectedConnectionId}
              busy={busy}
              onSwitchAccount={switchAccount}
              onDisconnect={disconnectAccount}
            />
            <DialogFooter className="gap-2 sm:gap-0 flex-wrap">
              {!status.connected ? (
                <Button onClick={() => connectDrive(false)} disabled={busy} data-testid="cloud-connect-btn">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Cloud size={14} />}
                  Connect Google Drive
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => connectDrive(true)}
                    disabled={busy}
                    data-testid="cloud-add-account-btn"
                  >
                    Add another account
                  </Button>
                  <Button onClick={() => setStep(2)} disabled={!selectedConnectionId} data-testid="cloud-browse-btn">
                    Browse files
                  </Button>
                </>
              )}
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-1">
            {driveAccountBar}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1 min-w-0">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runSearch()}
                  placeholder="Search files…"
                  className="h-10 sm:h-8 pl-7 text-[12px] w-full min-w-0"
                  data-testid="cloud-search-input"
                />
              </div>
              <Button size="sm" variant="outline" className="h-10 sm:h-8 w-full sm:w-auto shrink-0" onClick={runSearch}>Search</Button>
            </div>

            {!searchMode && (
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500 min-w-0">
                {breadcrumb.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] shrink-0 px-2"
                    onClick={goToParentFolder}
                    disabled={busy}
                    title={
                      breadcrumb[breadcrumb.length - 2]?.name
                        ? `Back to ${breadcrumb[breadcrumb.length - 2].name}`
                        : "Back one folder"
                    }
                    data-testid="cloud-folder-back"
                  >
                    <ChevronLeft size={13} />
                    Back
                  </Button>
                )}
                <div className="flex flex-wrap items-center gap-1 min-w-0">
                  {breadcrumb.map((c, i) => (
                    <span key={`${c.id}-${i}`} className="flex items-center gap-1">
                      {i > 0 && <span>/</span>}
                      <button
                        type="button"
                        className={cn(
                          "hover:text-emerald-700 font-medium",
                          i === breadcrumb.length - 1 && "text-slate-800",
                        )}
                        onClick={() => goBreadcrumb(i)}
                        disabled={busy || i === breadcrumb.length - 1}
                      >
                        {c.name}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-slate-100">
              <CloudBrowseItemList
                busy={busy}
                items={items}
                selected={selected}
                searchMode={searchMode}
                onToggleSelect={toggleSelect}
                onOpenFolder={openFolder}
              />
            </div>

            {nextPageToken && (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-[11px]"
                disabled={busy}
                onClick={() => loadBrowse(folderId, nextPageToken, searchMode ? { search: searchQ } : {})}
              >
                Load more
              </Button>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {canGoBack && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[11px] sm:mr-auto"
                  onClick={goToPreviousStep}
                  disabled={busy || importing}
                  data-testid="cloud-wizard-back-footer"
                >
                  <ChevronLeft size={14} />
                  Back
                </Button>
              )}
              <span className={cn("text-[11px] text-slate-500", !canGoBack && "sm:mr-auto")}>
                {selectedCount} selected (max 30 files per import)
              </span>
              <Button
                onClick={runImport}
                disabled={busy || importing || selectedCount === 0}
                data-testid="cloud-import-btn"
              >
                {importing ? (
                  <><Loader2 size={14} className="animate-spin" /> Importing…</>
                ) : (
                  "Import selected"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (() => {
          const syncInfo = describeSync(syncResult, sourceDetail);
          const readyCount = importResults.filter((r) => r.status === "ready").length;
          const activeDownloading = importing && !processingFlags.downloading;
          const activeProcessing = importing && processingFlags.downloading && !processingFlags.processing;
          const activeChunked = importing && processingFlags.processing && !processingFlags.chunked;
          const activeEmbedded = importing && processingFlags.chunked && !processingFlags.embedded;
          const activeIndexed = importing && processingFlags.embedded && !processingFlags.indexed;
          const activeSync = importing && processingFlags.indexed && !processingFlags.synced;

          return (
            <div className="space-y-3 py-2">
              {pendingFiles.length > 0 && (
                <ul className="text-[11px] text-slate-600 rounded-lg border border-slate-100 bg-white p-2 max-h-24 overflow-y-auto">
                  {pendingFiles.map((name) => (
                    <li key={name} className="truncate">{name}</li>
                  ))}
                </ul>
              )}
              <div className="space-y-3 rounded-lg border border-slate-200 p-4 bg-slate-50/50" data-testid="cloud-import-progress">
                <StepIndicator
                  done={processingFlags.downloading}
                  active={activeDownloading}
                  label="Downloading from Google Drive"
                  hint={pendingFiles.length ? `${pendingFiles.length} item(s) selected` : undefined}
                />
                <StepIndicator
                  done={processingFlags.processing}
                  active={activeProcessing}
                  label="Processing documents"
                />
                <StepIndicator
                  done={processingFlags.chunked}
                  active={activeChunked}
                  label="Chunking complete"
                />
                <StepIndicator
                  done={processingFlags.embedded}
                  active={activeEmbedded}
                  label="Embeddings generated"
                />
                <StepIndicator
                  done={processingFlags.indexed}
                  active={activeIndexed}
                  label="Knowledge indexed in Sellix Brain"
                  hint={readyCount ? `${readyCount} file(s) ready` : undefined}
                />
                <StepIndicator
                  done={syncInfo.ok || processingFlags.synced}
                  active={activeSync}
                  label={importing ? "Syncing to agent workspace…" : syncInfo.label}
                  hint={importing ? "May take a few seconds" : syncInfo.hint}
                />
              </div>
              {!importing && importComplete && (
                <div className="space-y-3">
                  <p className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1.5">
                    Import complete — knowledge is automatically available to all AI agents.
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {importResults.map((r) => {
                      let hint;
                      if (r.status === "ready") {
                        hint = "Indexed";
                      } else if (r.status === "skipped") {
                        hint = r.error || "Skipped";
                      } else {
                        hint = r.error || "Failed";
                      }
                      return (
                        <StepIndicator
                          key={r.filename}
                          done={r.status === "ready" || r.status === "skipped"}
                          label={r.filename}
                          hint={hint}
                        />
                      );
                    })}
                  </div>
                  <DialogFooter className="gap-2">
                    {canGoBack && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={goToPreviousStep}
                        disabled={busy}
                        data-testid="cloud-wizard-back-footer"
                      >
                        <ChevronLeft size={14} />
                        Back
                      </Button>
                    )}
                    <Button onClick={finish} disabled={busy} className="ml-auto" data-testid="cloud-finish-btn">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Done
                    </Button>
                  </DialogFooter>
                </div>
              )}
              {!importing && error && !importComplete && (
                <DialogFooter>
                  <Button variant="outline" onClick={goToPreviousStep}>
                    <ChevronLeft size={14} />
                    Back to browse
                  </Button>
                </DialogFooter>
              )}
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

export default CloudImportWizard;

const driveAccountPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  account: PropTypes.string,
});

DriveAccountBar.propTypes = {
  accountLabel: PropTypes.string,
  accounts: PropTypes.arrayOf(driveAccountPropType).isRequired,
  selectedConnectionId: PropTypes.string,
  busy: PropTypes.bool,
  onAddAccount: PropTypes.func.isRequired,
  onSwitchAccount: PropTypes.func.isRequired,
};

ConnectStatusBlock.propTypes = {
  connected: PropTypes.bool,
  accounts: PropTypes.arrayOf(driveAccountPropType).isRequired,
  account: PropTypes.string,
  selectedConnectionId: PropTypes.string,
  busy: PropTypes.bool,
  onSwitchAccount: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
};

CloudImportWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  provider: PropTypes.string,
  onComplete: PropTypes.func,
  initialConnected: PropTypes.bool,
};
